import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Stack,
  Tooltip
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";

import Card from "../../Components/Card";
import ResourceTable, { IGenerateColumns } from "../../Components/ResourceTable";
import { accessProfileToSelectList, users } from "../../Requests";
import PreserveUser, { IOptions } from "./PreserveUser";
import RemoveUser from "./RemoveUser";
import useGlobalContext from "../../Hooks/useGlobalContext";

const Users: React.FC = () => {
  const { setLoading: setLoadingGlobal } = useGlobalContext();

  const [accessProfilesOptions, setAccessProfilesOptions] = useState<IOptions[]>([]);
  const [rechargeRequest, setRechargeRequest] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [idUserEdit, setIdUserEdit] = useState<number | null>(null);
  const [idUserRemove, setIdUserRemove] = useState(0);

  const reloadResourceTable = () => setRechargeRequest((n) => n + 1);

  const handleDeleteUser = (setLoading: Dispatch<SetStateAction<boolean>>) => async (id: number) => {
    setIdUserRemove(id);
  };

  const handleEditUser = (setLoading: Dispatch<SetStateAction<boolean>>) => async (id: number) => {
    setLoadingGlobal?.(true);
    setOpenModal(true);
    setIdUserEdit(id);
    setLoading(false);
    setLoadingGlobal?.(false);
  };

  const handleOpenNewUser = () => {
    setOpenModal(true);
    setIdUserEdit(null);
  };

  useEffect(() => {
    const fetchProfiles = async () => {
      if (accessProfilesOptions.length === 0) {
        const result = await accessProfileToSelectList.get();
        setAccessProfilesOptions(
          (result || []).map((profile: any) => ({
            id: profile.id,
            label: profile.description
          }))
        );
      }
    };

    fetchProfiles();
  }, [accessProfilesOptions.length]);

  const generateColumns: IGenerateColumns = ({ setLoading }) => [
    {
      field: "name",
      headerName: "Nome",
      minWidth: 250,
      flex: 3
    },
    {
      field: "email",
      headerName: "E-mail",
      minWidth: 250,
      flex: 3
    },
    {
      field: "access_profile_id",
      headerName: "Perfil de Acesso",
      minWidth: 250,
      flex: 1,
      valueGetter: (params: any) => {
        const id = params;
        const profile = accessProfilesOptions.find((p) => p.id === id);
        return profile ? profile.label : "";
      }
    },
    {
      field: "acao_editar",
      headerName: "Editar",
      align: "center",
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => (
        <Tooltip title="Editar usuário">
          <Box
            sx={{ cursor: "pointer" }}
            onClick={() => handleEditUser(setLoading)(params.row.identifier)}
            aria-label="Editar usuário"
          >
            <EditIcon sx={{ color: "#1c2e4a" }} />
          </Box>
        </Tooltip>
      )
    },
    {
      field: "acao_remover",
      headerName: "Remover",
      align: "center",
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => (
        <Tooltip title="Remover usuário">
          <Box
            sx={{ cursor: "pointer" }}
            onClick={() => handleDeleteUser(setLoading)(params.row.identifier)}
            aria-label="Remover usuário"
          >
            <DeleteForeverIcon sx={{ color: "#dc3545" }} />
          </Box>
        </Tooltip>
      )
    }
  ];

  return (
    <Grid container spacing={0.5}>
      <Grid item xs={12} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleIcon />}
          onClick={handleOpenNewUser}
          aria-label="Adicionar novo usuário"
        >
          Adicionar
        </Button>

        {accessProfilesOptions.length > 0 && (
          <PreserveUser
            reloadResourceTable={reloadResourceTable}
            openModal={openModal}
            setOpenModal={setOpenModal}
            accessProfilesOptions={accessProfilesOptions}
            idUserEdit={idUserEdit}
          />
        )}
      </Grid>

      <Card
        titleDashboard="Listagem de Usuários"
        icon="PeopleIcon"
        paperConfig={{ minHeight: 704 }}
        marginTop="0"
        lineDividerHeader={false}
      >
        <ResourceTable
          request={users}
          generateColumns={generateColumns}
          pageSizeOptions={[15]}
          getRowId={(row) => row.identifier}
          minHeight={630}
          rechargeRequest={rechargeRequest}
          autoPageSize
          filterDefinitions={({ page, recordsPerPage }) => ({ page, recordsPerPage })}
        />
      </Card>

      {idUserRemove > 0 && (
        <RemoveUser
          idUser={idUserRemove}
          reloadResourceTable={reloadResourceTable}
          close={() => setIdUserRemove(0)}
        />
      )}
    </Grid>
  );
};

export default Users;
